import React, { useEffect, useState } from 'react';
import { Box, Text, Flex, Button } from '@chakra-ui/react';
import {
  ButtonIcon,
  FormFeedback,
  OutlineCard,
  useToast
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IFiguresAndTablesFile } from '@publishing-engine/domain/models';
import { FieldError, useFieldArray, useFormContext } from 'react-hook-form';
import FormFieldGraphicalAbstractImage from 'src/components/FormField/FormFieldGraphicalAbstractImage';
import { faPlus, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { DeleteFigureModal } from 'src/components/Submission/GenericFieldComponents/Figures/DeleteFigureModal';
import { FormFieldGraphicalAbstractImage as FormFieldGraphicalAbstractImageType } from '@publishing-engine/domain/types/article';
import { getValidationTypedMessage } from 'src/components/FormField/common/errorMessages';
import { FieldLabel } from 'src/components/Field';

interface GraphicalAbstractImageProps {
  config: FormFieldGraphicalAbstractImageType;
  submissionId: string;
  isFromEditModal?: boolean;
}

export type IGraphicalAbstractImageValue = {
  fileSet: {
    GraphicalAbstractImage?: IFiguresAndTablesFile;
  };
};

const GraphicalAbstractImage: React.FC<GraphicalAbstractImageProps> = ({
  config,
  submissionId,
  isFromEditModal
}) => {
  const toast = useToast();
  const methods = useFormContext<IGraphicalAbstractImageValue>();

  const { control } = methods;

  const fieldName = config.caseDataFields.main;

  // The title is the article type's label for the field, and SubmissionSidebar
  // scrolls to a section by looking that label up as a DOM id, so the label has
  // to stay the id of the heading.
  const fieldLabel = config.params.labels.main || 'Graphical Abstract';
  const validations = config.params.validations;
  // A required array is given `minItems: 1` by the schema even when no minimum
  // is configured, so either one makes the field mandatory to the submitter.
  const isFieldRequired =
    Boolean(validations.required) || (validations.minItems ?? 0) > 0;

  // The graphical abstract is mandatory for some article types, and the schema
  // reports that as an error on the array itself rather than on a row. Nothing
  // rendered it, so a submission was blocked with no visible reason when the
  // user had never opened the section.
  const graphicalAbstractErrors = methods.formState.errors?.fileSet
    ?.GraphicalAbstractImage as FieldError | FieldError[] | undefined;
  const graphicalAbstractError =
    graphicalAbstractErrors && !Array.isArray(graphicalAbstractErrors)
      ? graphicalAbstractErrors
      : undefined;

  const { fields, append, remove, move, update } = useFieldArray({
    control,
    name: fieldName as any
  });

  const [selectedFigures, setSelectedFigures] = useState<
    Record<string, boolean>
  >({});
  const [deleteFigures, setDeleteFigures] = useState<boolean>(false);
  const [showFiguresForm, setShowFiguresForm] = useState(false);

  const deleteSelected = () => {
    const indexesToDelete = fields
      .map((field, index) => (selectedFigures[field.id] ? index : -1))
      .filter(i => i !== -1);

    if (indexesToDelete.length === 0) return;

    const allSelected = indexesToDelete.length === fields.length;

    if (allSelected) {
      remove([...indexesToDelete]);
      setSelectedFigures({});
      setDeleteFigures(false);
      toast({
        variant: 'success',
        title: `${indexesToDelete.length} image${
          indexesToDelete.length > 1 ? 's' : ''
        } deleted successfully.`
      });
      setShowFiguresForm(false);
      return;
    }

    remove(indexesToDelete);

    setSelectedFigures(() => {
      const updated: Record<string, boolean> = {};
      fields.slice(0, fields.length - indexesToDelete.length).forEach(f => {
        updated[f.id] = false;
      });
      return updated;
    });

    toast({
      variant: 'success',
      title: `${indexesToDelete.length} image${
        indexesToDelete.length > 1 ? 's' : ''
      } deleted successfully.`
    });
    setDeleteFigures(false);
  };

  const toggleRow = (id: string) => {
    setSelectedFigures(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleSelectAll = () => {
    const allSelected = fields.every(f => selectedFigures[f.id]);
    const updated: Record<string, boolean> = {};

    fields.forEach(f => {
      updated[f.id] = !allSelected;
    });

    setSelectedFigures(updated);
  };

  useEffect(() => {
    const values = methods.getValues();

    if (
      Array.isArray(values?.fileSet?.GraphicalAbstractImage) &&
      values.fileSet.GraphicalAbstractImage.length > 0
    ) {
      setShowFiguresForm(true);
    }
  }, [methods]);

  return (
    <OutlineCard width="100%" padding={{ base: 3, md: 4 }}>
      <Flex flexDirection="column" width="100%" gap={{ base: 3, md: 4 }}>
        <Flex
          flexDirection="row"
          alignItems={{ base: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          width="100%"
          gap={{ base: 2, md: 0 }}
        >
          <FieldLabel
            label={isFromEditModal ? `Update ${fieldLabel}` : fieldLabel}
            subText=""
            isRequired={isFieldRequired}
            textStyle={isFromEditModal ? 'headingSection' : 'headingItem'}
            helpText={config.params.labels.helpText}
          />

          <span>
            <ButtonIcon
              size="regular"
              ariaLabel="right-delete-button"
              icon={<FontAwesomeIcon icon={faTrashAlt} />}
              onClick={() => {
                if (!Object.values(selectedFigures).some(x => x)) {
                  toast({
                    variant: 'attention',
                    title: 'No image selected'
                  });
                  return;
                }
                setDeleteFigures(true);
              }}
              w={{ base: '44px', md: 'auto' }}
            />
          </span>
        </Flex>
        <Flex flexDirection="column" gap={{ base: 2, md: 1 }}>
          {!showFiguresForm ? (
            <Box textAlign={{ base: 'left', md: 'center' }} width="100%">
              <Text textStyle="context" mb={4}>
                Click on 'Add Graphical Abstract' to add images
              </Text>
              <Button
                variant="primary"
                onClick={() => setShowFiguresForm(true)}
                gap={2}
                width={{ base: '100%', sm: 'auto' }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add Graphical Abstract
              </Button>
            </Box>
          ) : (
            <Box textAlign={{ base: 'left', md: 'center' }}>
              <FormFieldGraphicalAbstractImage
                config={config}
                submissionId={submissionId}
                fields={fields}
                append={append}
                selected={selectedFigures}
                toggleRow={toggleRow}
                toggleSelectAll={toggleSelectAll}
                methods={methods}
                move={move}
                update={update}
              />
            </Box>
          )}
        </Flex>

        {graphicalAbstractError && (
          <FormFeedback variant="error">
            {getValidationTypedMessage(
              fieldLabel,
              validations.kind,
              validations,
              graphicalAbstractError
            )}
          </FormFeedback>
        )}

        {deleteFigures && (
          <DeleteFigureModal
            onClose={() => setDeleteFigures(false)}
            isOpen={deleteFigures}
            onDelete={deleteSelected}
            selectedFigures={selectedFigures}
            filedName="Image"
          />
        )}
      </Flex>
    </OutlineCard>
  );
};

export default GraphicalAbstractImage;
